import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { addDays } from 'date-fns';

import type { ApiError, CommunicationsApi, CreateCommunicationParams } from '@izimi/api';
import { convertLocalDateToUTCDate } from '@izimi/utils';

import {
  useOptimisticSharedItemsCommunicationsDeleteCacheUpdate,
  useOptimisticSharedItemsCommunicationSeenCacheUpdate,
  useOptimisticSharedItemsDocumentsCopyCacheUpdate,
} from '../../shared';
import { useInvalidateCommunicationsCache } from '../cache';
import CommunicationsQueryKeys from '../CommunicationsQueryKeys';
import {
  useOptimisticCommunicationDocumentsCopyCacheUpdate,
  useOptimisticCommunicationsDocumentsCopyCacheUpdate,
} from './cache';
import { mapCommunicationsToEntities, mapCommunicationToEntity } from './mappers';
import type { PagedCommunicationsArgs } from './types';

const communicationsQueryKeys = new CommunicationsQueryKeys();

type CreateCommunicationsParams = {
  userIds: Array<string>;
} & Omit<CreateCommunicationParams, 'receiver'>;

/**
 * @deprecated Entity-based communications module backed by the legacy `@izimi/api` FetchService.
 * New work should use `createCommunicationsModule` (generated `@fednot/certinot-api` client +
 * raw `BoxCommunicationDetails`). This module is kept only so web/notary can migrate gradually.
 */
const createLegacyCommunicationsModule = ({
  communicationsApi,
}: {
  communicationsApi: CommunicationsApi;
}) => {
  const useCreateCommunications = () =>
    useMutation<unknown, ApiError, CreateCommunicationsParams>({
      mutationFn: ({ userIds, ...params }) =>
        Promise.all(
          userIds.map((receiver) => communicationsApi.createCommunication({ ...params, receiver })),
        ),
    });

  const useCreateCommunication = () =>
    useMutation<unknown, ApiError, CreateCommunicationParams>({
      mutationFn: (params) => communicationsApi.createCommunication(params),
    });

  const useGetCommunications = ({
    enabled = true,
    pageSize = 15,
    type = 'communications',
    params,
  }: PagedCommunicationsArgs = {}) => {
    const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
      useInfiniteQuery({
        queryKey: communicationsQueryKeys.getCommunications(type, params),
        queryFn: ({ pageParam }) =>
          communicationsApi.getCommunications({
            offset: Number(pageParam),
            ...params,
            limit: pageSize,
          }),
        enabled,
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
          const totalFetchedCommunications = allPages.flatMap((page) => page.communications).length;

          return totalFetchedCommunications < lastPage.total
            ? allPages.length * pageSize
            : undefined;
        },
      });

    const allCommunications = useMemo(
      () => mapCommunicationsToEntities(data?.pages.flatMap((page) => page.communications)),
      [data?.pages],
    );

    return {
      communications: allCommunications,
      total: data?.pages[0]?.total ?? 0,
      error,
      isLoading,
      refetch,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    };
  };

  const useGetCommunication = ({
    communicationId,
    markAsSeen = true,
  }: {
    communicationId?: string | null;
    markAsSeen?: boolean;
  }) => {
    const { markSharedItemCommunicationAsSeen } =
      useOptimisticSharedItemsCommunicationSeenCacheUpdate();
    const { data, isLoading, error, refetch } = useQuery({
      queryKey: communicationsQueryKeys.getCommunication(communicationId ?? ''),
      queryFn: async () => {
        const res = await communicationsApi.getCommunication(communicationId ?? '');

        if (markAsSeen && communicationId) {
          markSharedItemCommunicationAsSeen({ communicationId }); // Automatically set by the API;
        }

        return mapCommunicationToEntity(res);
      },
      enabled: Boolean(communicationId),
    });

    return {
      communication: data,
      isLoading,
      error,
      refetch,
    };
  };

  const useCopyCommunicationDocuments = () => {
    const { onMutate: handleSharedItemsMutation, onError: handleSharedItemsMutationError } =
      useOptimisticSharedItemsDocumentsCopyCacheUpdate();
    const { onMutate: handleCommunicationsMutation, onError: handleCommunicationsMutationError } =
      useOptimisticCommunicationsDocumentsCopyCacheUpdate();
    const { onMutate: handleCommunicationMutation, onError: handleCommunicationMutationError } =
      useOptimisticCommunicationDocumentsCopyCacheUpdate();

    const { mutateAsync, isPending, error } = useMutation({
      mutationFn: ({
        communicationId,
        documentIds,
      }: {
        communicationId: string;
        documentIds: Array<string>;
      }) => communicationsApi.copyCommunicationDocuments(communicationId, documentIds),
      onMutate: async (args) => ({
        ...(await handleSharedItemsMutation(args)),
        ...(await handleCommunicationsMutation(args)),
        ...(await handleCommunicationMutation(args)),
      }),
      onError: (error, variable, context) => {
        handleSharedItemsMutationError(error, variable, context);
        handleCommunicationsMutationError(error, variable, context);
        handleCommunicationMutationError(error, variable, context);
      },
    });

    return {
      copyCommunicationDocuments: mutateAsync,
      isPending,
      error,
    };
  };

  const useDownloadCommunicationDocument = () => {
    const { mutateAsync, isPending, error } = useMutation<
      Blob,
      ApiError,
      {
        communicationId: string;
        documentId: string;
        isDownloadRequest?: boolean;
      }
    >({
      mutationFn: ({ communicationId, documentId, isDownloadRequest = false }) =>
        communicationsApi.downloadCommunicationDocument(communicationId, documentId, {
          preview: !isDownloadRequest,
        }),
    });

    return {
      downloadCommunicationDocument: mutateAsync,
      error,
      isPending,
    };
  };

  const EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS = 31;

  const useGetExpiringCommunications = ({ pageSize = 15 }: { pageSize?: number } = {}) =>
    useGetCommunications({
      type: 'expiring',
      pageSize,
      params: {
        beforeScheduledDeleteDate: convertLocalDateToUTCDate(
          addDays(new Date(), EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS),
        ).toISOString(),
      },
    });

  const useDeleteCommunications = () => {
    const invalidateCommunicationsCache = useInvalidateCommunicationsCache('communications');
    const invalidateExpiringCommunicationsCache = useInvalidateCommunicationsCache('expiring');
    const invalidatePreviewCommunicationsCache = useInvalidateCommunicationsCache('preview');
    const { onMutate, onError, onSuccess } =
      useOptimisticSharedItemsCommunicationsDeleteCacheUpdate();
    const { mutateAsync, isPending, error } = useMutation({
      mutationFn: ({ communicationIds }: { communicationIds: Array<string> }) =>
        communicationsApi.deleteCommunications(communicationIds),
      onMutate,
      onError,
      onSuccess: () => {
        invalidateCommunicationsCache();
        invalidateExpiringCommunicationsCache();
        invalidatePreviewCommunicationsCache();
        onSuccess();
      },
    });

    return {
      deleteCommunications: mutateAsync,
      error,
      isPending,
    };
  };

  return {
    useDeleteCommunications,
    useCreateCommunication,
    useCreateCommunications,
    useGetCommunications,
    useGetCommunication,
    useGetExpiringCommunications,
    useCopyCommunicationDocuments,
    useDownloadCommunicationDocument,
  };
};

export default createLegacyCommunicationsModule;
